import { useState, useCallback, useEffect } from "react";

export const useUndo = () => {
  const [undoState, setUndoState] = useState({
    action: null,
    undoFunction: null,
    redoFunction: null,
    timeoutId: null,
    isUndoPerformed: false,
    isProcessing: false,
  });

  const clearUndo = useCallback(() => {
    if (undoState.timeoutId) {
      clearTimeout(undoState.timeoutId);
    }
    setUndoState({
      action: null,
      undoFunction: null,
      redoFunction: null,
      timeoutId: null,
      isUndoPerformed: false,
      isProcessing: false,
    });
  }, [undoState.timeoutId]);

  const saveUndoData = useCallback(({ action, undoFunction, redoFunction }) => {
    if (undoState.timeoutId) {
      clearTimeout(undoState.timeoutId);
    }
    const timeoutId = setTimeout(() => {
      setUndoState({
        action: null,
        undoFunction: null,
        redoFunction: null,
        timeoutId: null,
        isUndoPerformed: false,
        isProcessing: false,
      });
    }, 15000);
    setUndoState({
      action,
      undoFunction,
      redoFunction,
      timeoutId,
      isUndoPerformed: false,
      isProcessing: false,
    });
  }, [undoState.timeoutId]);

  const handleUndoRedo = useCallback(async () => {
    setUndoState(prev => ({ ...prev, isProcessing: true }));
    
    try {
      if (undoState.isUndoPerformed) {
        await Promise.resolve(undoState.redoFunction?.());
        clearUndo();
      } else {
        await Promise.resolve(undoState.undoFunction?.());
        if (undoState.timeoutId) {
          clearTimeout(undoState.timeoutId);
        }
        const newTimeoutId = setTimeout(() => {
          setUndoState({
            action: null,
            undoFunction: null,
            redoFunction: null,
            timeoutId: null,
            isUndoPerformed: false,
            isProcessing: false,
          });
        }, 15000);
        setUndoState(prev => ({
          ...prev,
          isUndoPerformed: true,
          timeoutId: newTimeoutId,
          isProcessing: false,
        }));
      }
    } catch (error) {
      setUndoState(prev => ({ ...prev, isProcessing: false }));
      throw error;
    }
  }, [undoState.isUndoPerformed, undoState.undoFunction, undoState.redoFunction, undoState.timeoutId, clearUndo]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const isEditableElement = () => {
        const tag = event.target?.tagName?.toLowerCase();
        return (
          event.target?.isContentEditable ||
          tag === 'input' ||
          tag === 'textarea'
        );
      };
      
      if (isEditableElement()) return;
      
      const hasModifier = event.ctrlKey || event.metaKey;
      const isZKey = event.code === 'KeyZ';
      
      const isUndoKeyCombo = hasModifier && isZKey && !event.shiftKey;
      const isRedoKeyCombo = hasModifier && isZKey && event.shiftKey;
      
      const canUndo = undoState.undoFunction && !undoState.isUndoPerformed;
      const canRedo = undoState.redoFunction && undoState.isUndoPerformed;
      
      const shouldExecute = !undoState.isProcessing && ((isUndoKeyCombo && canUndo) || (isRedoKeyCombo && canRedo));
      
      if (shouldExecute) {
        event.preventDefault();
        handleUndoRedo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [undoState.undoFunction, undoState.redoFunction, undoState.isUndoPerformed, undoState.isProcessing, handleUndoRedo]);

  return {
    // State
    undoState,
    setUndoState,
    
    // Functions
    saveUndoData,
    handleUndoRedo,
  };
};
